import type { AgGridReact } from "@jsq/ds/core";
import { DdqProdOpsWorkflowStatus, DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import { STATUS_LABELS, WORKFLOW_TYPE_LABELS } from "../labels";
import { AssigneeRenderer, OfferingRenderer, StatusRenderer } from "./cellRenderers";
import {
  compareDueAt,
  formatCreatedAtValue,
  formatDueDateValue,
  formatWorkflowTypeValue,
  getClientName,
  getOfferingNames,
} from "./columnHelpers";
import * as styles from "./styles_vanilla.css";
import type { DdqQueueRow } from "../types";
import type { ValueFormatterParams } from "ag-grid-community";

// Every other column stays unsortable/unfilterable -- these are the only fields the backend
// accepts a sortBy/filter value for.
export const columnDefs: AgGridReact.AgGridReactProps<DdqQueueRow>["columnDefs"] = [
  {
    field: "title",
    headerName: "DDQ",
    sortable: true,
    filter: false,
    width: 260,
    cellClass: styles.cellFlexCenter,
    tooltipField: "title",
  },
  {
    colId: "client",
    headerName: "Client",
    sortable: false,
    filter: false,
    width: 200,
    valueGetter: getClientName,
    cellClass: styles.cellFlexCenter,
  },
  {
    field: "workflowType",
    headerName: "Request type",
    sortable: false,
    filter: "agSetColumnFilter",
    filterParams: {
      buttons: ["reset"],
      values: Object.values(DdqProdOpsWorkflowType),
      valueFormatter: (params: ValueFormatterParams<DdqQueueRow, string>) =>
        WORKFLOW_TYPE_LABELS[params.value as DdqProdOpsWorkflowType] ?? params.value ?? "",
    },
    width: 160,
    valueFormatter: formatWorkflowTypeValue,
    cellClass: styles.cellFlexCenter,
  },
  {
    colId: "offerings",
    headerName: "Offering",
    sortable: false,
    filter: false,
    width: 240,
    valueGetter: getOfferingNames,
    cellRenderer: OfferingRenderer,
    cellClass: styles.cellFlexCenter,
  },
  {
    colId: "assignee",
    headerName: "Assignee",
    sortable: false,
    filter: false,
    width: 160,
    cellRenderer: AssigneeRenderer,
    cellClass: styles.cellFlexCenter,
  },
  {
    field: "status",
    headerName: "Status",
    sortable: false,
    // Completed is stripped centrally in useDdqQueueFilters regardless of what this reports --
    // ag-grid's Reset re-checks every box, so this control alone can't be trusted to exclude it.
    filter: "agSetColumnFilter",
    filterParams: {
      buttons: ["reset"],
      values: Object.values(DdqProdOpsWorkflowStatus),
      valueFormatter: (params: ValueFormatterParams<DdqQueueRow, string>) =>
        STATUS_LABELS[params.value as DdqProdOpsWorkflowStatus] ?? params.value ?? "",
    },
    width: 150,
    cellRenderer: StatusRenderer,
    cellClass: styles.cellFlexCenter,
  },
  {
    field: "dueAt",
    headerName: "Due date",
    sortable: true,
    sort: "asc",
    comparator: compareDueAt,
    filter: false,
    width: 210,
    valueFormatter: formatDueDateValue,
    cellClass: styles.cellDate,
  },
  {
    field: "createdAt",
    headerName: "Created",
    sortable: true,
    filter: false,
    width: 210,
    valueFormatter: formatCreatedAtValue,
    cellClass: styles.cellDate,
  },
];
